'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';

/**
 * Props for `CategoryCardGrid`.
 */
export type CategoryCardGridProps =
  SliceComponentProps<Content.CategoryCardGridSlice>;

/**
 * Arrow icon for learn points
 */
const ArrowIcon: FC<{ className?: string }> = ({ className }) => (
  <span className={className}>→</span>
);

/**
 * Component for "CategoryCardGrid" Slices.
 *
 * Displays a section with:
 * - Title and description at the top
 * - 2x2 grid of category cards with icons
 * - Footer note
 * - "You'll learn" section with bullet points
 */
const CategoryCardGrid: FC<CategoryCardGridProps> = ({ slice }) => {
  const { title, description, categories, footer_note, learn_points } =
    slice.primary;

  const headerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const learnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const elementsToAnimate = [
      { ref: headerRef, delay: '0ms' },
      { ref: cardsRef, delay: '150ms' },
      { ref: footerRef, delay: '300ms' },
      { ref: learnRef, delay: '400ms' },
    ];

    elementsToAnimate.forEach(({ ref, delay }) => {
      if (ref.current) {
        ref.current.classList.add(
          'opacity-0',
          'translate-y-8',
          'transition-all',
          'duration-700',
          'ease-out',
        );
        ref.current.style.transitionDelay = delay;
        observer.observe(ref.current);
      }
    });

    return () => {
      elementsToAnimate.forEach(({ ref }) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative bg-[#0E1010] py-16 md:py-20 lg:py-28 px-4 md:px-8 lg:px-[150px]"
    >
      <div className="max-w-[1040px] mx-auto flex flex-col gap-10 items-center">
        {/* Header Section */}
        <div
          ref={headerRef}
          className="flex flex-col gap-6 items-center w-full"
        >
          {/* Title */}
          {isFilled.richText(title) && (
            <div className="text-[28px] md:text-[40px] font-bold leading-[1.4] text-white text-center w-full">
              <PrismicRichText
                field={title}
                components={{
                  heading2: ({ children }) => <h2>{children}</h2>,
                }}
              />
            </div>
          )}

          {/* Description */}
          {isFilled.richText(description) && (
            <div className="text-base md:text-lg font-normal leading-[1.5] text-white/90 w-full">
              <PrismicRichText field={description} />
            </div>
          )}
        </div>

        {/* Category Cards Grid */}
        {categories && categories.length > 0 && (
          <div
            ref={cardsRef}
            className="w-full flex flex-wrap gap-6 justify-center"
          >
            {categories.map((category, index) => (
              <div
                key={index}
                className="w-full md:w-[calc(50%-12px)] max-w-[508px] backdrop-blur-[10px] bg-gradient-to-b from-[#002726] to-transparent rounded-2xl p-6 flex flex-col gap-3 items-center shadow-[0px_4.325px_64.871px_0px_rgba(0,0,0,0.08)]"
              >
                {/* Icon + Title Row */}
                <div className="flex gap-3 items-center">
                  {isFilled.image(category.icon) && (
                    <div className="w-12 h-[33px] flex-shrink-0">
                      <PrismicNextImage
                        field={category.icon}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  )}
                  {category.title && (
                    <span className="text-lg md:text-xl font-bold text-[#EEEEEE] leading-[1.4] whitespace-nowrap">
                      {category.title}
                    </span>
                  )}
                </div>

                {/* Card Description */}
                {category.description && (
                  <p className="text-base md:text-lg font-normal leading-[1.5] text-white text-center">
                    {category.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Footer Note */}
        {isFilled.richText(footer_note) && (
          <div
            ref={footerRef}
            className="text-base md:text-lg font-normal leading-[1.5] text-white/90 text-center max-w-[746px]"
          >
            <PrismicRichText field={footer_note} />
          </div>
        )}

        {/* Learn Points Section */}
        {learn_points && learn_points.length > 0 && (
          <div
            ref={learnRef}
            className="w-full bg-gradient-to-b from-[rgba(0,0,0,0.4)] to-transparent rounded-2xl p-6 flex flex-col gap-6"
          >
            {/* Learn Section Title */}
            <div className="w-full">
              <h3 className="text-xl md:text-2xl font-bold leading-[1.4] text-white text-center">
                You&apos;ll learn how to:
              </h3>
            </div>

            {/* Learn Points List */}
            <div className="w-full flex flex-col gap-1.5 text-center">
              {learn_points.map((point, index) => (
                <div
                  key={index}
                  className="text-base md:text-lg leading-[1.5] text-white"
                >
                  <span className="font-bold text-[#05F273]">
                    <ArrowIcon />
                  </span>
                  {isFilled.richText(point.text) && (
                    <PrismicRichText
                      field={point.text}
                      components={{
                        paragraph: ({ children }) => (
                          <span className="font-normal">{children}</span>
                        ),
                        strong: ({ children }) => (
                          <span className="font-bold">{children}</span>
                        ),
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default CategoryCardGrid;
